import { ReactNode } from 'react';

import {
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  OutlinedInputProps,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { EndAdornmentWithStatusIcon } from '../utils/EndAdornmentWithStatusIcon';
import { FullTextTooltip } from '@/components/ui/FullTextTooltip';
import { InfoIconWithTooltip } from '@/components/ui/InfoIconWithTooltip';
import { useFieldContext } from '@/forms/formContext';
import { BaseInputFieldProps } from '@/forms/types';

// Source: https://gist.github.com/spryzone/efe664e0564b3c66f2fb09e7207a8d54
const passwordManagerIgnore = {
  'data-1p-ignore': true, // 1Password
  'data-lpignore': 'true', // LastPass
  'data-protonpass-ignore': 'true', // ProtonPass
  'data-bwignore': '1', // Bitwarden
};

interface TextInputFieldProps
  extends
    BaseInputFieldProps,
    Omit<
      OutlinedInputProps,
      | 'value'
      | 'onChange'
      | 'onBlur'
      | 'name'
      | 'id'
      | 'label'
      | 'required'
      | 'multiline'
    > {
  type?: 'text' | 'email' | 'password' | 'number';
  helperText?: string | ReactNode;
}

export const TextInputField = ({
  required,
  label,
  placeholder,
  type,
  showIsValid = false,
  infoTooltip,
  helperText,
  ...props
}: TextInputFieldProps) => {
  const field = useFieldContext();

  const isValid = !!(field.state.meta.isValid && field.state.meta.isDirty);
  const isError = !!(
    (!field.state.meta.isValid && field.state.meta.isDirty) ||
    (!field.state.meta.isValid && field.form.state.submissionAttempts > 0)
  );

  const { t } = useTranslation('forms');

  const infoTooltipId = infoTooltip ? `id_${field.name}_tooltip` : undefined;
  const helperTextId =
    helperText && !isError ? `id_${field.name}_helper` : undefined;
  const displayError = isError ? field.state.meta.errors[0] : null;
  const displayErrorId = displayError ? `id_${field.name}_error` : null;
  const describedByIds = [infoTooltipId, helperTextId, displayErrorId]
    .filter(Boolean)
    .join(' ');

  return (
    <FormControl valid={showIsValid && isValid} error={isError}>
      <Stack direction="row">
        <FullTextTooltip>
          <InputLabel required={required} htmlFor={`id_${field.name}`}>
            {label}
          </InputLabel>
        </FullTextTooltip>
        {infoTooltip && infoTooltipId && (
          <InfoIconWithTooltip id={infoTooltipId} tooltip={infoTooltip} />
        )}
      </Stack>
      <OutlinedInput
        {...props}
        name={field.name}
        inputProps={{
          id: `id_${field.name}`,
          ...(describedByIds && {
            'aria-describedby': describedByIds,
          }),
          ...(type === 'number' && {
            style: {
              textAlign: 'right',
            },
            className: 'TextAlignRight', // Identifier class for conditional styling in theme
          }),
          ...(props.autoComplete === 'off' && { ...passwordManagerIgnore }),
        }}
        value={field.state.value}
        placeholder={placeholder}
        onBlur={field.handleBlur}
        onChange={(e) => field.handleChange(e.target.value)}
        {...(((showIsValid && isValid) || isError || props.endAdornment) && {
          endAdornment: (
            <EndAdornmentWithStatusIcon
              isValid={showIsValid && isValid}
              isError={isError}
              endAdornment={props.endAdornment}
            />
          ),
        })}
      />
      {isError && displayError && displayErrorId ? (
        <FormHelperText error id={displayErrorId}>
          {displayError.message?.startsWith('validationErrors.')
            ? t(displayError.message, {
                value: label ?? t('validationErrors.DefaultValueLabel'),
              })
            : displayError.message}
        </FormHelperText>
      ) : null}

      {helperText && !isError && (
        <FormHelperText
          id={helperTextId}
          component={typeof helperText === 'string' ? 'p' : 'div'}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};
